import React from 'react';

/**
 * SparklineChart — minimalist SVG sparkline used as Insight card footer chart.
 *
 * Props:
 * - data: number[] — series of values (>=2)
 * - color: string (default Homly orange)
 * - width / height: px
 * - showDot: boolean — show pulsing dot on last point
 * - className: extra classes
 */
export default function SparklineChart({
    data = [],
    color = '#FF914D',
    width = 120,
    height = 32,
    showDot = true,
    className = '',
}) {
    if (!data || data.length < 2) {
        return <div className={className} style={{ width, height }} />;
    }

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = width / (data.length - 1);
    const points = data
        .map((v, i) => {
            const x = i * stepX;
            const y = height - ((v - min) / range) * (height - 4) - 2;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');

    // Build path for fill area
    const areaPath =
        `M0,${height} ` +
        data
            .map((v, i) => {
                const x = i * stepX;
                const y = height - ((v - min) / range) * (height - 4) - 2;
                return `L${x.toFixed(1)},${y.toFixed(1)}`;
            })
            .join(' ') +
        ` L${width},${height} Z`;

    const lastX = (data.length - 1) * stepX;
    const lastY = height - ((data[data.length - 1] - min) / range) * (height - 4) - 2;

    const gradId = `spark-grad-${Math.random().toString(36).slice(2, 9)}`;

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className={className}
            data-testid="sparkline"
        >
            <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.4" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={areaPath} fill={`url(#${gradId})`} />
            <polyline
                points={points}
                fill="none"
                stroke={color}
                strokeWidth="1.5"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            {showDot && (
                <>
                    <circle
                        cx={lastX}
                        cy={lastY}
                        r="3.5"
                        fill={color}
                        opacity="0.25"
                        className="animate-ping origin-center"
                        style={{ transformOrigin: `${lastX}px ${lastY}px` }}
                    />
                    <circle cx={lastX} cy={lastY} r="2" fill={color} />
                </>
            )}
        </svg>
    );
}
